import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SeoHead from "../../Components/SeoHead";
import Hero from "../../Components/Hero";
import { getAllPosts } from "src/utils/apicalls";
import { Prisma } from "@prisma/client";
import { Link } from "react-router";
import { blogSchema, webPageSchema } from "src/utils/seoSchema";
import { useLocale } from "src/i18n/LocaleProvider";
import { DEFAULT_LOCALE } from "src/i18n/localesConfig";

type PostWithAuthor = Prisma.PostGetPayload<{
  include: {
    author: true; // Include the related author
  };
}>;

const BlogPostCard: React.FC<{ post: PostWithAuthor }> = ({ post }) => {
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = Boolean(post.coverUrl) && !coverFailed;

  return (
    <Card
      sx={{
        maxWidth: 350,
        width: "100%",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {showCover && (
        <CardMedia
          component="img"
          height="140"
          image={post.coverUrl}
          alt={post.title}
          loading="lazy"
          onError={() => setCoverFailed(true)}
          sx={{ borderRadius: "4px 4px 0 0" }}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          gutterBottom
          variant="h6"
          component="h3"
          sx={{ fontWeight: "bold", color: "#fff" }}
        >
          {post.title}
        </Typography>

        <Typography variant="body2" sx={{ color: "#94a3b8" }}>
          {post.description}
        </Typography>

        <Typography variant="caption" sx={{ color: "#64748b" }}>
          {new Date(post.createdAt).toLocaleDateString()}
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {post.tags?.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      </CardContent>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Link
          to={`/blog/${post.slug}`}
          style={{ textDecoration: "none", width: "100%" }}
        >
          <Button fullWidth size="small" variant="contained" color="primary">
            Read More
          </Button>
        </Link>
      </Box>
    </Card>
  );
};

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<PostWithAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const { locale } = useLocale();

  useEffect(() => {
    // Fetch all posts
    getAllPosts(
      (res) => {
        setPosts(res as PostWithAuthor[]);
        setLoading(false);
      },
      (err) => console.log("Error fetching posts:", err)
    );
  }, []);

  const blogDescription =
    "Insights on HOA compliance, STR enforcement, and short-term rental monitoring for cities and local governments.";

  return (
    <>
      <SeoHead
        title="Latest Blog Posts | STR Monitoring"
        description={blogDescription}
        keywords="blog, short-term rentals, HOA compliance, STR enforcement, local government, short term rental monitoring, city ordinances"
        canonical="/blog"
        ogTitle="Latest Blog Posts | Short-Term Rental Monitoring"
        ogDescription="Insights on HOA compliance, STR enforcement, and community protection for cities and local governments."
        ogImage="/images/blog-hero.jpg"
        alternateRssHref="/rss.xml"
        skipHreflang
        robots={locale === DEFAULT_LOCALE ? undefined : "noindex, follow"}
        jsonLd={[
          webPageSchema({
            name: "Latest Blog Posts",
            description: blogDescription,
            url: "/blog",
            image: "/images/blog-hero.jpg",
          }),
          blogSchema({
            description: blogDescription,
            posts: posts.map((post) => ({
              title: post.title,
              description: post.description,
              coverUrl: post.coverUrl,
              slug: post.slug,
              createdAt: String(post.createdAt),
              authorName: post.author?.name,
            })),
          }),
        ]}
      />
      <Hero
        title={"Latest Blog Posts"}
        subTitle="Insights on HOA compliance, STR enforcement, and community protection."
        minHeight="auto"
        buttonText="Contact Us for Information"
        buttonLink="/contact"
        button={false}
        showRadar={false}
      />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 8 }}>
          <CircularProgress />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10, px: 2, color: "#94a3b8" }}>
          <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
            No posts yet
          </Typography>
          <Typography variant="body2">
            Check back soon for compliance insights and product updates.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            backgroundColor: "#0b0f17",
            maxWidth: "100%",
            mx: "auto",
            width: "100%",
          }}
          component="section"
          aria-labelledby="blog-list"
        >
          <Grid
            container
            spacing={3}
            mt={2}
            justifyContent="start"
            alignItems="stretch"
            sx={{ maxWidth: 1280, mx: "auto" }}
          >
            {posts.map((post) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 3 }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
                mb={2}
                key={post.id}
              >
                <BlogPostCard post={post} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default BlogList;
